import {
  batchPDFService,
  getResultDetails,
  type BatchPDFAsyncStatusResponse,
} from "@/services/api/pdf";
import type { SinglePDFAsyncRequest } from "@/services/api/pdf/types";
import {
  createLocalPdfProgress,
  mapTaskStatusToPdfProgress,
  resetPdfProgressTracking,
  type PdfGenerationUiState,
} from "@/utils/pdfGenerationStages";
import { setPdfProgressTaskId } from "@/utils/pdfGenerationProgress";

export interface RunAsyncPdfDownloadParams {
  pdfRequest: SinglePDFAsyncRequest;
  onProgress?: (state: PdfGenerationUiState) => void;
}

function emit(
  onProgress: RunAsyncPdfDownloadParams["onProgress"],
  state: PdfGenerationUiState,
) {
  onProgress?.(state);
}

function mapStatus(status: BatchPDFAsyncStatusResponse): PdfGenerationUiState {
  return mapTaskStatusToPdfProgress({
    task_id: status.task_id,
    status: status.status,
    stage: status.stage,
    status_message: status.status_message,
    progress_percentage: status.progress_percentage,
    steps: status.steps,
    error: status.error,
  });
}

/** Trigger browser download from a blob. */
export function downloadPdfBlob(blob: Blob, filename: string): void {
  const downloadUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = downloadUrl;
  anchor.download = filename.endsWith(".pdf") ? filename : `${filename}.pdf`;
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(downloadUrl);
  document.body.removeChild(anchor);
}

/**
 * Queue async single-PDF generation, poll for staged progress, then download.
 */
export async function runAsyncPdfDownload({
  pdfRequest,
  onProgress,
}: RunAsyncPdfDownloadParams): Promise<void> {
  resetPdfProgressTracking();
  emit(onProgress, createLocalPdfProgress("requesting", "Submitting request…"));

  const start = await batchPDFService.startAsyncSingle(pdfRequest);
  const taskId = start.task_id;
  setPdfProgressTaskId(taskId);

  emit(
    onProgress,
    createLocalPdfProgress("configuring", "Preparing report configuration…"),
  );

  try {
    const finalStatus = await batchPDFService.pollTaskStatus(
      taskId,
      (status) => emit(onProgress, mapStatus(status)),
    );

    emit(onProgress, createLocalPdfProgress("downloading", "Downloading PDF…"));

    const details = getResultDetails(finalStatus.result);
    const filename =
      details?.filename || pdfRequest.filename || `${pdfRequest.id}.pdf`;

    await batchPDFService.downloadTaskResult(taskId, finalStatus.result, filename);

    emit(onProgress, createLocalPdfProgress("complete", "Download complete"));
  } finally {
    setPdfProgressTaskId(null);
  }
}
